import { useEffect, useMemo, useState } from "react";
import { api, errorMessage, formatDate } from "../Assets/assetsShared";
import "./Leave.css";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const STATUS_FILTERS = ["Pending", "Approved", "Rejected", "Cancelled", "All"];

const dateText = (leave) =>
  leave.halfDay ||
  String(leave.fromDate).slice(0, 10) === String(leave.toDate).slice(0, 10)
    ? `${formatDate(leave.fromDate)}${leave.halfDay ? " (Half day)" : ""}`
    : `${formatDate(leave.fromDate)} → ${formatDate(leave.toDate)}`;

const initials = (name) =>
  String(name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");

const countCell = (value) =>
  value ? value : <span className="lm-zero">–</span>;

export default function LeaveManagement() {
  const [tab, setTab] = useState("requests");
  const [error, setError] = useState("");

  /* ----- requests ----- */
  const [requests, setRequests] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState("Pending");
  const [search, setSearch] = useState("");
  const [loadingRequests, setLoadingRequests] = useState(true);

  /* ----- report (empty = server picks the latest option with entries) ----- */
  const [view, setView] = useState("month");
  const [fy, setFy] = useState("");
  const [month, setMonth] = useState("");
  const [quarter, setQuarter] = useState("");
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);

  /* ----- single employee report ----- */
  const [employeeId, setEmployeeId] = useState("");
  const [empFy, setEmpFy] = useState("");
  const [empMonth, setEmpMonth] = useState("all");
  const [empData, setEmpData] = useState(null);
  const [loadingEmp, setLoadingEmp] = useState(false);

  /* ================= REQUESTS ================= */
  const loadRequests = async (status = statusFilter) => {
    try {
      const res = await api.get("/leaves", {
        params: status === "All" ? {} : { status },
      });

      setRequests(res.data.leaves || []);
      setPendingCount(res.data.pendingCount || 0);
    } catch (requestError) {
      setError(errorMessage(requestError, "Unable to load leave requests."));
    } finally {
      setLoadingRequests(false);
    }
  };

  useEffect(() => {
    loadRequests(statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleReview = async (leave, status) => {
    let note = "";

    if (status === "Rejected") {
      note = window.prompt("Reason for rejecting this leave?") || "";
      if (!note.trim()) return;
    }

    try {
      setError("");
      await api.patch(`/leaves/${leave._id}/review`, {
        status,
        note: note.trim(),
      });
      await loadRequests();
    } catch (requestError) {
      setError(errorMessage(requestError, "Unable to update leave."));
    }
  };

  const visibleRequests = useMemo(() => {
    const value = search.trim().toLowerCase();

    return requests.filter(
      (leave) =>
        !value ||
        [
          leave.user?.name,
          leave.user?.email,
          leave.leaveType,
          leave.festival,
          leave.reason,
        ]
          .map((text) => String(text || "").toLowerCase())
          .some((text) => text.includes(value))
    );
  }, [requests, search]);

  /* ================= REPORT (all employees) ================= */
  useEffect(() => {
    if (tab !== "report") return;

    const loadReport = async () => {
      try {
        setLoadingReport(true);
        setError("");

        const res = await api.get("/leaves/report", {
          params: {
            view,
            fy: fy || undefined,
            month: month || undefined,
            quarter: quarter || undefined,
          },
        });

        setReport(res.data);

        // keep the filters in sync with what really exists
        if (String(res.data.fy) !== String(fy)) setFy(String(res.data.fy));
        if (res.data.month && String(res.data.month) !== String(month)) {
          setMonth(String(res.data.month));
        }
        if (res.data.quarter && String(res.data.quarter) !== String(quarter)) {
          setQuarter(String(res.data.quarter));
        }
      } catch (requestError) {
        setError(errorMessage(requestError, "Unable to load leave report."));
      } finally {
        setLoadingReport(false);
      }
    };

    loadReport();
  }, [tab, view, fy, month, quarter]);

  /* ================= REPORT (one employee) ================= */
  useEffect(() => {
    if (tab !== "report" || !employeeId) return;

    const loadEmployee = async () => {
      try {
        setLoadingEmp(true);
        setError("");

        const res = await api.get(`/leaves/employee/${employeeId}`, {
          params: { fy: empFy || undefined, month: empMonth },
        });

        setEmpData(res.data);

        if (String(res.data.fy) !== String(empFy)) setEmpFy(String(res.data.fy));
        if (String(res.data.month) !== String(empMonth)) {
          setEmpMonth(String(res.data.month));
        }
      } catch (requestError) {
        setError(errorMessage(requestError, "Unable to load employee report."));
      } finally {
        setLoadingEmp(false);
      }
    };

    loadEmployee();
  }, [tab, employeeId, empFy, empMonth]);

  const openEmployee = (id) => {
    setEmployeeId(String(id));
    setEmpFy("");
    setEmpMonth("all");
    setEmpData(null);
  };

  const closeEmployee = () => {
    setEmployeeId("");
    setEmpData(null);
  };

  const noEntries = report && !(report.fyOptions || []).length;

  // totals of the selected employee for the selected financial year
  const empSummary = empData?.summaries?.find(
    (item) => String(item.fy) === String(empData.fy)
  ) || { taken: 0, pending: 0, rejected: 0 };

  return (
    <div className="lm-page">
      {/* ================= HERO ================= */}
      <div className="lm-hero">
        <div>
          <h1>Leave Management</h1>
          <p>Approve employee leave requests and view the employee-wise report.</p>
        </div>

        <div className="lm-hero-stat">
          <strong>{pendingCount}</strong>
          <span>
            Pending
            <br />
            approvals
          </span>
        </div>
      </div>

      {error && <div className="lm-alert">{error}</div>}

      <div className="lm-tabs">
        <button
          type="button"
          className={`lm-tab ${tab === "requests" ? "active" : ""}`}
          onClick={() => setTab("requests")}
        >
          Leave Requests
          {pendingCount > 0 && <span className="lm-count">{pendingCount}</span>}
        </button>

        <button
          type="button"
          className={`lm-tab ${tab === "report" ? "active" : ""}`}
          onClick={() => setTab("report")}
        >
          Employee Report
        </button>
      </div>

      {/* ================= REQUESTS TAB ================= */}
      {tab === "requests" && (
        <>
          <div className="lm-toolbar">
            <input
              className="lm-search"
              placeholder="Search employee, leave type or reason..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />

            <div className="lm-pills">
              {STATUS_FILTERS.map((status) => (
                <button
                  type="button"
                  key={status}
                  className={`lm-pill ${statusFilter === status ? "active" : ""}`}
                  onClick={() => setStatusFilter(status)}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>

          {loadingRequests ? (
            <div className="lm-empty">Loading...</div>
          ) : !visibleRequests.length ? (
            <div className="lm-empty">No leave requests found.</div>
          ) : (
            <div className="lm-list">
              {visibleRequests.map((leave) => (
                <div
                  key={leave._id}
                  className={`lm-req lm-req-${leave.status}`}
                >
                  <div className="lm-avatar">{initials(leave.user?.name)}</div>

                  <div>
                    <div className="lm-req-top">
                      <strong>{leave.user?.name || "-"}</strong>
                      <span>{leave.user?.role}</span>
                    </div>

                    <div className="lm-req-line">
                      <span className="lm-type">
                        {leave.leaveType}
                        {leave.festival ? ` · ${leave.festival}` : ""}
                      </span>
                      <span className="lm-dates">{dateText(leave)}</span>
                      <span className="lm-days">
                        {leave.days} {leave.days === 1 ? "day" : "days"}
                      </span>
                    </div>

                    <p className="lm-reason">{leave.reason}</p>

                    <p className="lm-meta">
                      Applied on {formatDate(leave.createdAt)}
                      {leave.reviewedBy &&
                        ` · ${leave.status} by ${leave.reviewedBy.name}${
                          leave.reviewedAt
                            ? ` on ${formatDate(leave.reviewedAt)}`
                            : ""
                        }`}
                      {leave.reviewNote && ` · “${leave.reviewNote}”`}
                    </p>
                  </div>

                  <div className="lm-req-side">
                    <span className={`lm-badge lm-badge-${leave.status}`}>
                      {leave.status}
                    </span>

                    {leave.status === "Pending" && (
                      <div className="lm-actions">
                        <button
                          type="button"
                          className="lm-btn approve"
                          onClick={() => handleReview(leave, "Approved")}
                        >
                          Approve
                        </button>

                        <button
                          type="button"
                          className="lm-btn reject"
                          onClick={() => handleReview(leave, "Rejected")}
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ================= REPORT TAB ================= */}
      {tab === "report" && (
        <>
          {loadingReport && !report && <div className="lm-empty">Loading...</div>}

          {noEntries && <div className="lm-empty">No leave entries yet.</div>}

          {report && !noEntries && (
            <>
              <div className="lm-toolbar">
                <label className="lm-filter">
                  Employee
                  <select
                    value={employeeId}
                    onChange={(e) =>
                      e.target.value ? openEmployee(e.target.value) : closeEmployee()
                    }
                  >
                    <option value="">All employees</option>
                    {report.rows.map((row) => (
                      <option key={row.userId} value={row.userId}>
                        {row.name}
                      </option>
                    ))}
                  </select>
                </label>

                {!employeeId && (
                  <>
                    <label className="lm-filter">
                      View by
                      <select value={view} onChange={(e) => setView(e.target.value)}>
                        <option value="month">Monthly</option>
                        <option value="quarter">Quarterly</option>
                        <option value="fy">Financial Year</option>
                      </select>
                    </label>

                    <label className="lm-filter">
                      Financial Year
                      <select
                        value={fy}
                        onChange={(e) => {
                          setFy(e.target.value);
                          setMonth("");
                          setQuarter("");
                        }}
                      >
                        {report.fyOptions.map((option) => (
                          <option key={option.fy} value={option.fy}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>

                    {view === "month" && (
                      <label className="lm-filter">
                        Month
                        <select
                          value={month}
                          onChange={(e) => setMonth(e.target.value)}
                        >
                          {report.monthOptions.map((value) => (
                            <option key={value} value={value}>
                              {MONTH_NAMES[value - 1]}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}

                    {view === "quarter" && (
                      <label className="lm-filter">
                        Quarter
                        <select
                          value={quarter}
                          onChange={(e) => setQuarter(e.target.value)}
                        >
                          {report.quarterOptions.map((item) => (
                            <option key={item.value} value={item.value}>
                              {item.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                  </>
                )}

                {employeeId && empData && (
                  <>
                    <label className="lm-filter">
                      Financial Year
                      <select
                        value={empFy}
                        onChange={(e) => {
                          setEmpFy(e.target.value);
                          setEmpMonth("all");
                        }}
                      >
                        {(empData.fyOptions.length
                          ? empData.fyOptions
                          : [{ fy: empData.fy, label: empData.fyLabel }]
                        ).map((option) => (
                          <option key={option.fy} value={option.fy}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="lm-filter">
                      Month
                      <select
                        value={empMonth}
                        onChange={(e) => setEmpMonth(e.target.value)}
                      >
                        <option value="all">All Months</option>
                        {(empData.monthOptions || []).map((value) => (
                          <option key={value} value={value}>
                            {MONTH_NAMES[value - 1]}
                          </option>
                        ))}
                      </select>
                    </label>
                  </>
                )}
              </div>

              {/* ---------- ONE EMPLOYEE ---------- */}
              {employeeId && (
                <>
                  {loadingEmp && !empData && (
                    <div className="lm-empty">Loading...</div>
                  )}

                  {empData && (
                    <>
                      <div className="lm-emp-head">
                        <div className="lm-avatar">
                          {initials(empData.employee?.name)}
                        </div>

                        <div className="lm-emp-head-info">
                          <h2>{empData.employee?.name}</h2>
                          <p>
                            {empData.employee?.role}
                            {empData.employee?.email
                              ? ` · ${empData.employee.email}`
                              : ""}
                          </p>
                        </div>

                        <button
                          type="button"
                          className="lm-back"
                          onClick={closeEmployee}
                        >
                          ← All employees
                        </button>
                      </div>

                      <p className="lm-period">
                        {empData.fyLabel}
                        {empMonth !== "all"
                          ? ` · ${MONTH_NAMES[Number(empMonth) - 1]}`
                          : ""}{" "}
                        - Leave taken
                      </p>

                      <div className="lm-kpis">
                        <div className="lm-kpi">
                          <small>Total days taken ({empData.fyLabel})</small>
                          <strong>{empSummary.taken}</strong>
                        </div>

                        {Object.entries(empData.typeTotals || {}).map(
                          ([type, days]) => (
                            <div className="lm-kpi" key={type}>
                              <small>{type}</small>
                              <strong>{days}</strong>
                            </div>
                          )
                        )}

                        <div className="lm-kpi warn">
                          <small>Pending requests</small>
                          <strong>{empSummary.pending}</strong>
                        </div>

                        <div className="lm-kpi bad">
                          <small>Rejected requests</small>
                          <strong>{empSummary.rejected}</strong>
                        </div>
                      </div>

                      {!empData.leaves.length ? (
                        <div className="lm-empty">
                          This employee has no leave requests in this period.
                        </div>
                      ) : (
                        <div className="lm-list">
                          {empData.leaves.map((leave) => (
                            <div
                              key={leave._id}
                              className={`lm-req lm-req-solo lm-req-${leave.status}`}
                            >
                              <div>
                                <div className="lm-req-line">
                                  <span className="lm-type">
                                    {leave.leaveType}
                                    {leave.festival ? ` · ${leave.festival}` : ""}
                                  </span>
                                  <span className="lm-dates">{dateText(leave)}</span>
                                  <span className="lm-days">
                                    {leave.days} {leave.days === 1 ? "day" : "days"}
                                  </span>
                                </div>

                                <p className="lm-reason">{leave.reason}</p>

                                <p className="lm-meta">
                                  Applied on {formatDate(leave.createdAt)}
                                  {leave.reviewedBy &&
                                    ` · ${leave.status} by ${leave.reviewedBy.name}${
                                      leave.reviewedAt
                                        ? ` on ${formatDate(leave.reviewedAt)}`
                                        : ""
                                    }`}
                                  {leave.reviewNote && ` · “${leave.reviewNote}”`}
                                </p>
                              </div>

                              <div className="lm-req-side">
                                <span
                                  className={`lm-badge lm-badge-${leave.status}`}
                                >
                                  {leave.status}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              {/* ---------- ALL EMPLOYEES ---------- */}
              {!employeeId && (
                <>
                  <p className="lm-period">{report.label} - Approved leave days</p>

                  <div className="lm-kpis">
                    <div className="lm-kpi">
                      <small>Total days taken</small>
                      <strong>{report.totals.taken}</strong>
                    </div>

                    {report.leaveTypes.map((type) => (
                      <div className="lm-kpi" key={type}>
                        <small>{type}</small>
                        <strong>{report.totals.byType[type]}</strong>
                      </div>
                    ))}

                    <div className="lm-kpi warn">
                      <small>Pending requests</small>
                      <strong>{report.totals.pending}</strong>
                    </div>

                    <div className="lm-kpi bad">
                      <small>Rejected requests</small>
                      <strong>{report.totals.rejected}</strong>
                    </div>
                  </div>

                  <div className="lm-table-wrap">
                    <table className="lm-table">
                      <thead>
                        <tr>
                          <th>Employee</th>
                          <th>Role</th>
                          <th className="lm-num">Days Taken</th>
                          <th className="lm-num">Pending</th>
                          <th className="lm-num">Rejected</th>
                          {report.leaveTypes.map((type) => (
                            <th key={type} className="lm-num">
                              {type}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody>
                        {report.rows.map((row) => (
                          <tr key={row.userId}>
                            <td>
                              <div className="lm-emp">
                                <div className="lm-avatar">{initials(row.name)}</div>
                                <button
                                  type="button"
                                  className="lm-link"
                                  title="View this employee's leave report"
                                  onClick={() => openEmployee(row.userId)}
                                >
                                  {row.name}
                                </button>
                              </div>
                            </td>
                            <td>{row.role}</td>
                            <td className="lm-num">
                              <strong>{countCell(row.taken)}</strong>
                            </td>
                            <td className="lm-num">{countCell(row.pending)}</td>
                            <td className="lm-num">{countCell(row.rejected)}</td>
                            {report.leaveTypes.map((type) => (
                              <td key={type} className="lm-num">
                                {countCell(row.byType[type])}
                              </td>
                            ))}
                          </tr>
                        ))}

                        <tr className="lm-total-row">
                          <td colSpan="2">Total</td>
                          <td className="lm-num">{report.totals.taken}</td>
                          <td className="lm-num">{report.totals.pending}</td>
                          <td className="lm-num">{report.totals.rejected}</td>
                          {report.leaveTypes.map((type) => (
                            <td key={type} className="lm-num">
                              {report.totals.byType[type]}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}